import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';

/** localStorage key for the iOS home-screen icon badge count (not letter red dots). */
export const ICON_NOTIFICATIONS_STORAGE_KEY = 'icon_notifications_number';

/**
 * Manages the iOS app-icon badge count via Capacitor Badge.
 * Android and web only persist the count in localStorage for future API sync.
 */
@Injectable({
  providedIn: 'root',
})
export class AppIconBadgeService {
  private readonly router = inject(Router);

  private started = false;

  /** Current icon badge count mirrored in localStorage. */
  readonly count = signal<number>(this.readStoredCount());

  /** Clamp to a non-negative integer and persist; updates the native badge on iOS only. */
  async setCount(count: number): Promise<void> {
    const clamped = Math.max(0, Math.floor(count));
    localStorage.setItem(ICON_NOTIFICATIONS_STORAGE_KEY, String(clamped));
    this.count.set(clamped);

    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    try {
      await Badge.set({ count: clamped });
    } catch (err) {
      console.warn('AppIconBadge: Badge.set failed', err);
    }
  }

  /** Reset the icon badge to zero without touching letter or menu red-dot state. */
  async clear(): Promise<void> {
    await this.setCount(0);
  }

  /** Apply a badge count from push payload or API when it is a finite number. */
  async applyIncomingCount(count: number | null | undefined): Promise<void> {
    if (typeof count === 'number' && Number.isFinite(count)) {
      await this.setCount(count);
    }
  }

  /**
   * Register iOS foreground push handling (once).
   * Clears the badge when a push arrives while the user is already on Home.
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    void PushNotifications.addListener('pushNotificationReceived', (notification) => {
      void (async () => {
        if (typeof notification.badge === 'number') {
          await this.applyIncomingCount(notification.badge);
        }
        // Foreground push on Home should not leave a stuck SpringBoard badge.
        if (this.isHomeRoute(this.router.url)) {
          await this.clear();
        }
      })();
    });
  }

  /**
   * On Home, clear the badge; elsewhere on iOS, sync localStorage from the native icon count.
   */
  async syncOrClearForCurrentRoute(): Promise<void> {
    if (this.isHomeRoute(this.router.url)) {
      await this.clear();
      return;
    }

    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    try {
      const result = await Badge.get();
      await this.setCount(result.count);
    } catch (err) {
      console.warn('AppIconBadge: Badge.get failed', err);
    }
  }

  private readStoredCount(): number {
    const raw = localStorage.getItem(ICON_NOTIFICATIONS_STORAGE_KEY);
    if (raw === null) {
      return 0;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.max(0, Math.floor(parsed));
  }

  /** True when the router path is `/home` (query and fragment ignored). */
  private isHomeRoute(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return path === '/home' || path.startsWith('/home/');
  }
}
